<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { usePublicUserMenuItems } from '@/composables/usePublicNavItems';

const router = useRouter();
const authStore = useAuthStore();
const { t } = useI18n();
const isOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentMouseDown, true);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleDocumentMouseDown, true);
});

const menuItems = usePublicUserMenuItems();

const handleLogout = async () => {
  await authStore.logout();
  router.push('/');
  isOpen.value = false;
};

const closeMenu = () => {
  isOpen.value = false;
};

const navigateTo = async (to: string) => {
  closeMenu();
  await router.push(to);
};

const handleDocumentMouseDown = (event: MouseEvent) => {
  if (!isOpen.value) {
    return;
  }

  const target = event.target as Node | null;
  if (!target) {
    return;
  }

  if (menuRef.value && !menuRef.value.contains(target)) {
    closeMenu();
  }
};
</script>

<template>
  <div ref="menuRef" class="relative">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center gap-2 text-cream-100 hover:text-gold-400 transition-colors font-sans"
    >
      <span class="hidden sm:inline">{{ authStore.user?.name }}</span>
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition name="fade">
      <div
        v-if="isOpen"
        data-testid="user-menu-dropdown"
        class="absolute right-0 mt-2 w-56 bg-noir-900 rounded-xl border border-noir-700 shadow-2xl shadow-noir-950/50 py-2 z-50">
        <button
          v-for="item in menuItems"
          :key="item.key"
          type="button"
          data-testid="user-menu-item"
          class="w-full text-left block px-4 py-2.5 text-cream-100/80 hover:text-gold-400 hover:bg-noir-800 transition-colors font-sans text-sm"
          @click="navigateTo(item.to)"
        >
          {{ t(item.key) }}
        </button>
        <hr class="my-2 border-noir-700" />
        <button
          @click="handleLogout"
          class="w-full text-left px-4 py-2.5 text-burgundy-800 hover:bg-burgundy-800/10 transition-colors font-sans text-sm"
        >
          {{ t('nav.logout') }}
        </button>
      </div>
    </Transition>

  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
